import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { MdPersonSearch } from "react-icons/md";
import AtriumApi, { getApiErrorMessage } from "api";
import Button from "components/button";
import Card from "components/card";
import { PageContainer, PageHeader } from "components/page";
import Skeleton from "components/skeleton/Skeleton";
import { EmptyState, ErrorState } from "components/states";
import UserForm from "./user-form";

/**
 * Full-page person edit form.
 */
const EditUserPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadUser = useCallback(() => {
    setLoading(true);
    setError(null);
    AtriumApi.get(`/v1/users/get-user/${id}`)
      .then((response) => {
        setUser(response?.data?.user || null);
      })
      .catch((err) => {
        setError(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  useEffect(() => {
    if (!id) {
      navigate("/admin/people");
      return;
    }
    loadUser();
  }, [id, navigate, loadUser]);

  const profilePath = `/admin/people/${id}/profile`;
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ");

  return (
    <PageContainer variant="narrow">
      <PageHeader
        title="Edit Profile"
        description={fullName || undefined}
        backTo={profilePath}
        backLabel="Back to Profile"
      />
      {loading ? (
        <Card padding="md" aria-busy="true">
          <Skeleton lines={6} />
        </Card>
      ) : error ? (
        <Card padding="md">
          <ErrorState
            message={getApiErrorMessage(error, "This profile could not be loaded.")}
            onRetry={loadUser}
          />
        </Card>
      ) : !user ? (
        <Card padding="md">
          <EmptyState
            icon={MdPersonSearch}
            title="Person Not Found"
            description="This person may have been deleted or you may not have access."
            action={
              <Button as={Link} to="/admin/people" variant="secondary">
                Back to People
              </Button>
            }
          />
        </Card>
      ) : (
        <UserForm
          user={user}
          onClose={() => navigate(profilePath)}
          onSuccess={() => navigate(profilePath)}
        />
      )}
    </PageContainer>
  );
};

export default EditUserPage;
